import { Form, Formik } from 'formik';
import { createContext, useContext, useState } from 'react';
import { useHistory } from 'react-router';
import useSWR from 'swr';

import { APIList } from '../../../../../../typings/API.interface';
import {
  View,
  ViewCreateInput,
  ViewFilters,
  ViewType,
  ViewUpdateInput,
} from '../../../../../../typings/View.interface';
import APIMethodKeys from '../../../client/APIMethodKeys';
import field_formats from '../../../utils/field-formatters';
import isEmpty from '../../../utils/isEmpty';
import { formatPath } from '../../../utils/path';
import { useDialog } from '../../common/Dialog';
import TextInput from '../../common/Form/Fields/TextInput';
import Modal from '../../common/Modal';
import { useToasts } from '../../common/Toast';
import { GlobalContext } from '../../contexts/GlobalContext';
import { DashboardContext } from './DashboardContext';

export type IViewsContext = {
  views: APIList<View>;
  createView: (filters: object, dynamic_columns?: string[]) => void;
  updateView: (id: string, filters: object, dynamic_columns?: string[]) => void;
  renameView: (id: string) => void;
  deleteView: (id: string) => Promise<boolean>;
  duplicateView: (id: string) => Promise<View | undefined>;
};

export const ViewsContext = createContext<IViewsContext>({
  views: { models: [], pagination: { limit: 0 }, count: 0 },
  createView: () => {
    return;
  },
  updateView: () => {
    return;
  },
  renameView: () => {
    return;
  },
  deleteView: () => Promise.resolve(false),
  duplicateView: () => Promise.resolve(undefined),
});

const addCliView = (views: APIList<View>, user_id: string) => {
  if (!views) return views;
  return {
    ...views,
    count: views.count + 1,
    models: [
      ...views.models,
      {
        id: 'cli',
        name: 'CLI',
        type: 'events',
        filters: { cli_user_id: [user_id] },
      },
    ],
  };
};
export const ViewsContextProvider: React.FC<{
  type: ViewType;
  redirect_root?: string;
  children: React.ReactNode | ((context: IViewsContext) => React.ReactNode);
}> = ({ type, redirect_root, children }) => {
  const { HookdeckAPI } = useContext(GlobalContext);
  const { active_cli_sessions, user } = useContext(DashboardContext);
  const { data: _views, mutate } = useSWR(APIMethodKeys.views.list({ type }), () =>
    HookdeckAPI.views.list<View>({ type }),
  );

  const active_cli = active_cli_sessions.length > 0;
  const add_cli_view = (_views && active_cli) || location.pathname.includes('/cli');
  const views = add_cli_view ? addCliView(_views!, user!.id) : _views;

  const history = useHistory();
  const { addToast } = useToasts();
  const showDialog = useDialog();
  const [action, setAction] = useState<{
    action: 'create' | 'update';
    view?: View;
    filters?: ViewFilters;
    dynamic_columns?: string[];
  } | null>(null);

  const handleRedirect = (view?: View) => {
    if (redirect_root) {
      history.push(
        !view ? redirect_root : `${redirect_root}/${view.name.replace(/ /g, '-').toLowerCase()}`,
      );
    }
  };

  const mutateViews = (mutate_type: 'create' | 'update' | 'delete', new_view: View) => {
    if (!views) return;
    const new_views = views as APIList<View>;
    const index = new_views.models.findIndex((view) => view.id === new_view.id);
    switch (mutate_type) {
      case 'create':
        new_views.models = [new_view, ...new_views.models];
        new_views.count++;
        mutate(new_views);
        break;
      case 'update':
        new_views.models[index] = new_view;
        mutate(new_views);
        break;
      case 'delete':
        new_views.models.splice(index, 1);
        new_views.count--;
        mutate(new_views);
        break;
      default:
        return;
    }

    handleRedirect(mutate_type === 'delete' ? undefined : new_view);
  };

  const createView = (filters: ViewFilters, dynamic_columns?: string[]): void => {
    setAction({ action: 'create', filters, dynamic_columns });
  };

  const createViewCallback = async (
    name: string,
    filters: ViewFilters,
    dynamic_columns?: string[],
  ) => {
    return HookdeckAPI.views
      .create({ type, filters, name, dynamic_columns } as ViewCreateInput)
      .then((new_view) => {
        mutateViews('create', new_view);
        setAction(null);
        addToast('success', 'View created');
        return new_view;
      })
      .catch(() => {
        addToast('error', 'View failed to create');
      });
  };

  const updateView = async (id: string, filters: ViewFilters, dynamic_columns?: string[]) => {
    return HookdeckAPI.views
      .update(id, { filters, dynamic_columns } as ViewUpdateInput)
      .then((new_view) => {
        mutateViews('update', new_view);
        setAction(null);
        addToast('success', 'View updated');
        return new_view;
      })
      .catch(() => {
        addToast('error', 'View failed to update');
      });
  };

  const renameView = (id: string): void => {
    const view = views?.models.find((view) => view.id === id) as View;
    setAction({ action: 'update', view });
  };

  const renameViewCallback = (id: string, name: string) => {
    return HookdeckAPI.views
      .update(id, { name } as ViewUpdateInput)
      .then((new_view) => {
        mutateViews('update', new_view);
        setAction(null);
        addToast('success', 'View renamed');
        return new_view;
      })
      .catch(() => {
        addToast('error', 'View failed to rename');
      });
  };

  const deleteView = async (id: string): Promise<boolean> => {
    const view = views?.models.find((view) => view.id === id);
    if (!view) return false;
    return new Promise((resolve, reject) => {
      showDialog(
        () => {
          HookdeckAPI.views
            .delete(id)
            .then(() => {
              mutateViews('delete', view as View);
              addToast('success', 'View deleted');
              resolve(true);
            })
            .catch((e) => {
              addToast('error', 'Failed to remove view');
              reject(e);
            });
        },
        () => resolve(false),
        {
          title: 'Delete this view',
          message: `Are you sure you want to delete "${view.name}" view?`,
          danger: true,
        },
      );
    });
  };

  const duplicateView = async (id: string): Promise<View | undefined> => {
    const view = views?.models?.find((view) => view.id === id);
    if (!view || (views && views.count > 249)) return;
    const name = view.name.split(' Copy')[0];
    const count = (views?.models ?? []).filter(
      (other_view) => other_view.name.split(' Copy')[0] === name,
    ).length;
    const body = { ...view };
    body.name = `${name} Copy ${count < 2 ? '' : count}`;
    return HookdeckAPI.views
      .create(body as ViewCreateInput)
      .then((new_view) => {
        mutateViews('create', new_view);
        setAction(null);
        handleRedirect(new_view);
        addToast('success', 'View created');
        return new_view;
      })
      .catch((e) => {
        addToast('error', 'View failed to create');
        throw e;
      });
  };

  const context = {
    views: views as APIList<View>,
    renameView,
    createView,
    updateView,
    duplicateView,
    deleteView,
  };

  if (!views || !context.views) return null;

  return (
    <ViewsContext.Provider value={context}>
      {action && (
        <Formik
          initialValues={{ name: action.view?.name || '' }}
          validate={async (values) => {
            const errors: { name?: string } = {};
            const should_validate = !isEmpty(values.name) && values.name !== action.view?.name;
            if (should_validate) {
              const views = await HookdeckAPI.views.list({ name: values.name }).catch(() => ({
                models: [],
                count: 0,
              }));
              if (views.count > 0 && (!action.view?.id || action.view?.id !== views.models[0].id)) {
                errors.name = 'View name already in use';
              }
            }
            if (formatPath(values.name).toLocaleLowerCase() === 'cli') {
              errors.name = '"CLI" is a reserved view name';
            }

            return errors;
          }}
          onSubmit={(values) =>
            action?.action === 'update'
              ? renameViewCallback(action.view?.id as string, values.name)
              : createViewCallback(values.name, action.filters!, action.dynamic_columns)
          }>
          {({ handleSubmit }) => (
            <Form>
              <Modal
                title={action?.action === 'update' ? 'Rename this view' : 'Create a new view'}
                onClose={() => setAction(null)}
                submit_label={action?.action === 'update' ? 'Update' : 'Save'}
                cancel_label={'Cancel'}
                onSubmit={handleSubmit}
                onCancel={() => setAction(null)}>
                <TextInput format={field_formats.viewName} focus m={0} required name="name" />
              </Modal>
            </Form>
          )}
        </Formik>
      )}
      {typeof children === 'function' ? children(context) : children}
    </ViewsContext.Provider>
  );
};
